import { cfg } from 'config/runtimeConfig'
import { getSsoSession } from 'auth/ssoClient'

/**
 * What happened to this record inside the editions - the real-estate story (interest in a unit,
 * its stage, holds, bookings, payments, site visits, the e-sign trail) and the workflow engine's
 * record of events it took in for the lead and what it did with them.
 *
 * Both come from the editions service (REACT_APP_EDITIONS_API_URL), the only thing that reads
 * the editions store. Each is one GET; the two are merged here into one list, newest first, in
 * the shape the brief and the timeline read. Where the service is not configured the list is
 * empty, which the brief reads as "no edition activity", not as a channel it could not read.
 *
 * Times from these routes are UTC and carry a Z; parseWhen reads them as such.
 */

const EDITIONS_ORIGIN: string = (cfg('REACT_APP_EDITIONS_API_URL') ?? '').trim().replace(/\/+$/, '')
export const EDITIONS_ENABLED: boolean = EDITIONS_ORIGIN !== ''

export type EditionEventKind =
  | 'interest' | 'interest_stage' | 'interest_withdrawn'
  | 'hold' | 'hold_released' | 'hold_expired' | 'booked' | 'cancelled'
  | 'payment_received' | 'requirement_saved' | 'site_visit'
  | 'esign_sent' | 'esign_viewed' | 'esign_signed' | 'esign_declined'
  | 'workflow_event'

export interface EditionEvent {
  id: string
  /** ISO 8601, UTC. */
  at: string
  kind: EditionEventKind
  /** The unit, as people name it ("A-103"), with its tower and project where known. */
  unitNo: string
  tower: string
  project: string
  /** The row's own words: the interest stage, the hold note, the cancel reason, the event title. */
  detail: string
  amount: number | null
  /** Who did it, as the editions name them. */
  byName: string
  /** Workflow events only: what the engine did ("started", "no_match", ...) and the workflows it started. */
  result?: string
  workflows?: string[]
  /** Workflow events only: started workflows whose run then failed or was given up - nothing reached the customer. */
  failedWorkflows?: string[]
}

type Row = Record<string, unknown>
const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : v == null ? '' : String(v))
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

async function get(path: string, signal?: AbortSignal): Promise<Row> {
  const session = getSsoSession()
  if (!session.token || !session.userId || !session.parentId) throw new Error('not signed in')
  const query = new URLSearchParams({ userId: String(session.userId), parentId: String(session.parentId) })
  const response = await fetch(`${EDITIONS_ORIGIN}${path}${path.includes('?') ? '&' : '?'}${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${session.token}` },
    signal,
  })
  if (!response.ok) throw new Error(`editions answered ${response.status}`)
  return (await response.json()) as Row
}

function fromRealEstate(rows: unknown): EditionEvent[] {
  if (!Array.isArray(rows)) return []
  return rows.map((r, i) => {
    const row = r as Row
    return {
      id: `re-${text(row.kind)}-${text(row.unitId) || i}-${text(row.at)}`,
      at: text(row.at),
      kind: (text(row.kind) || 'interest') as EditionEventKind,
      unitNo: text(row.unitNo),
      tower: text(row.tower),
      project: text(row.project),
      detail: text(row.detail),
      amount: num(row.amount),
      byName: text(row.byName) || text(row.byLogin),
    }
  })
}

// A run that ended without doing its work. The engine's own words for it.
// the engine stores outcomes with a dash ("given-up", "step-failed"); run_status too
const RUN_FAILED_RE = /fail|given.?up|cancel|abort|dead/i

function fromWorkflow(rows: unknown, runRows: unknown): EditionEvent[] {
  if (!Array.isArray(rows)) return []
  const runs = new Map<string, Row>()
  if (Array.isArray(runRows)) for (const r of runRows as Row[]) runs.set(text(r.run_id), r)
  return rows.map((r) => {
    const row = r as Row
    const decisions = Array.isArray(row.decisions) ? (row.decisions as Row[]) : []
    const startedDecisions = decisions.filter((d) => /^start/i.test(text(d.decision)) && text(d.workflow_name) !== '')
    const failed = (d: Row) => {
      const run = runs.get(text(d.run_id))
      return !!run && (RUN_FAILED_RE.test(text(run.outcome)) || RUN_FAILED_RE.test(text(run.run_status)))
    }
    const started = startedDecisions.filter((d) => !failed(d)).map((d) => text(d.workflow_name))
    const failedWorkflows = startedDecisions.filter(failed).map((d) => text(d.workflow_name))
    return {
      id: `wf-${text(row.event_id)}`,
      at: text(row.occurred_at),
      kind: 'workflow_event',
      unitNo: '',
      tower: '',
      project: '',
      detail: text(row.title) || text(row.event_key),
      amount: null,
      byName: '',
      result: text(row.result),
      workflows: started,
      failedWorkflows,
    }
  })
}

/**
 * The record's edition activity, newest first. Leads only - the editions key everything by lead.
 * Resolves to [] when the service is not configured; rejects when it is and did not answer,
 * which the brief shows as a channel it could not read.
 */
export async function fetchEditionEvents(
  entityType: 'lead' | 'enquiry',
  entityId: string,
  signal?: AbortSignal,
): Promise<EditionEvent[]> {
  if (!EDITIONS_ENABLED || entityType !== 'lead' || !/^\d+$/.test(entityId)) return []
  const [re, wf] = await Promise.all([
    get(`/api/ed/re/prospect/timeline?leadId=${entityId}`, signal).then((d) => fromRealEstate(d.events)),
    // a workflow engine that is not switched on answers with an error; the real-estate story still counts
    get(`/api/ed/wf/leads/${entityId}/events?top=50`, signal).then((d) => fromWorkflow(d.events, d.runs), () => [] as EditionEvent[]),
  ])
  return [...re, ...wf]
    .filter((e) => e.at !== '')
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
}
